import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, ArrowRightLeft, Bed as BedIcon, CalendarClock, LogOut, Stethoscope } from 'lucide-react';
import { getAdmission, adviseDischarge } from '@/lib/ipdApi';
import { useToast } from '@/components/ui/use-toast';
import { usePermission } from '@/hooks/usePermission';

import IPDOverviewTab          from '@/components/IPD/IPDOverviewTab';
import IPDVitalsTab            from '@/components/IPD/IPDVitalsTab';
import IPDMedicationOrdersTab  from '@/components/IPD/IPDMedicationOrdersTab';
import IPDProgressNotesTab     from '@/components/IPD/IPDProgressNotesTab';
import IPDChargesTab           from '@/components/IPD/IPDChargesTab';
import IPDDischargeSummaryTab  from '@/components/IPD/IPDDischargeSummaryTab';
import IPDAttachmentsTab       from '@/components/IPD/IPDAttachmentsTab';
import { AdmissionSummary, BillingTotalsBar } from '@/components/IPD/IPDAdmissionBars';
import { PatientBanner } from '@/components/Patient/PatientBanner';
import { TransferModal, DischargeModal, DatesModal } from '@/components/IPD/IPDAdmissionModals';
import { usePageTitle } from "@/hooks/usePageTitle";

const TABS = ['overview', 'vitals', 'orders', 'notes', 'attachments', 'charges', 'discharge'] as const;
type Tab = typeof TABS[number];

export default function IPDAdmissionDetailPage() {
  usePageTitle('IPD admission');
  const { admissionId } = useParams<{ admissionId: string }>();
  const { can, hasFeature } = usePermission();
  const canWrite = can('ipd:write');
  // Doctor's acts: advising discharge (orders and summary sign-off are gated in their tabs)
  const canSign = can('ipd:clinical');
  const { toast } = useToast();
  const [advising, setAdvising] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const backTo = (location.state as { from?: string } | null)?.from || '/ipd';

  // An unknown ?tab= (old link) opens Overview rather than a blank page
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = (TABS as readonly string[]).includes(tabParam ?? '') ? tabParam as Tab : 'overview';
  const setTab    = (tab: Tab) => setSearchParams({ tab }, { replace: true });

  const [admission, setAdmission] = useState<any>(null);
  const [loading,   setLoading]   = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [billingRefresh, setBillingRefresh] = useState(0);

  const loadAdmission = useCallback(() => {
    if (!admissionId) return;
    return getAdmission(admissionId)
      .then(data => setAdmission(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [admissionId]);

  useEffect(() => { loadAdmission(); }, [loadAdmission]);

  // Transfers and dates change the bed charges
  const onStayChanged = () => { loadAdmission(); setBillingRefresh(v => v + 1); };

  const toggleAdvice = async (advise: boolean) => {
    if (!admissionId || advising) return;
    setAdvising(true);
    try {
      await adviseDischarge(admissionId, advise);
      await loadAdmission();
      toast({ title: advise ? 'Discharge advised' : 'Discharge advice withdrawn',
        description: advise ? 'Billing and the discharge summary can be completed now.' : undefined });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Could not save', description: e?.message });
    } finally {
      setAdvising(false);
    }
  };

  if (!admissionId) return null;

  if (!hasFeature('ipd')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BedIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">IPD Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          In-patient department management is not included in your current plan.
        </p>
      </div>
    );
  }

  const TAB_TRIGGER = 'rounded-none px-0 pb-2 pt-1 touch:min-h-10 text-sm whitespace-nowrap border-b-2 border-transparent text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';
  const backButton = (
    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 -ml-2 flex-shrink-0" onClick={() => navigate(backTo)} aria-label="Back">
      <ArrowLeft className="w-4 h-4" />
    </Button>
  );

  // Below lg the whole page scrolls, so the banner doesn't hold half a phone screen; from lg it stays and the tab scrolls
  return (
    <div className="flex flex-col h-full overflow-auto lg:overflow-hidden">

      {loading ? (
        <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-border shrink-0" aria-busy="true">
          {backButton}
          <div className="h-11 w-11 rounded-full bg-muted animate-pulse" />
          <div className="space-y-1.5">
            <div className="h-4 w-48 rounded bg-muted animate-pulse" />
            <div className="h-3 w-32 rounded bg-muted animate-pulse" />
          </div>
        </div>
      ) : !admission ? (
        <>
          <div className="flex items-center px-4 sm:px-6 py-3 border-b border-border shrink-0">{backButton}</div>
          <div className="flex flex-col items-center justify-center flex-1 text-center px-4">
            <AlertTriangle className="w-8 h-8 text-muted-foreground mb-2" />
            <h2 className="text-base font-semibold mb-1">Admission Not Found</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              This admission doesn't exist or you don't have access to it.
            </p>
            <Link to={backTo} className="text-sm text-primary mt-3 hover:underline">Back</Link>
          </div>
        </>
      ) : (
      <Tabs value={activeTab} onValueChange={v => setTab(v as Tab)} className="flex flex-col lg:flex-1 lg:min-h-0">
        {/* Who, where, what is owed — stays in view on every tab */}
        <div className="shrink-0 border-b border-border bg-card">
          <PatientBanner
            patient={{
              name: admission.patient_name || 'Unknown',
              age: admission.patient_age,
              gender: admission.patient_gender,
              blood_group: admission.patient_blood_group,
              mrn: admission.patient_mrn,
              phone: admission.patient_phone,
              allergies: admission.patient_allergies,
            }}
            meta={<span>IPD <span className="font-mono text-foreground/80">{admission.admission_number}</span></span>}
            leading={backButton}
            actions={(canWrite || canSign) && (admission.status === 'admitted' ? (
              <>
                {canSign && (admission.discharge_advised_at ? (
                  <Button variant="ghost" size="sm" disabled={advising} onClick={() => toggleAdvice(false)}>Withdraw advice</Button>
                ) : (
                  <Button variant="outline" size="sm" className="gap-1.5" disabled={advising} onClick={() => toggleAdvice(true)}>
                    <Stethoscope className="w-4 h-4" /> Advise discharge
                  </Button>
                ))}
                {canWrite && (
                  <>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setTransferOpen(true)}>
                      <ArrowRightLeft className="w-4 h-4" /> Transfer bed
                    </Button>
                    <Button size="sm" className="gap-1.5" onClick={() => setDischargeOpen(true)}>
                      <LogOut className="w-4 h-4" /> Discharge
                    </Button>
                  </>
                )}
              </>
            ) : canWrite && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDatesOpen(true)}>
                <CalendarClock className="w-4 h-4" /> Edit discharge
              </Button>
            ))}
          />
          <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3 px-4 sm:px-6 pb-3">
            <AdmissionSummary admission={admission} canWrite={canWrite} onDatesClick={() => setDatesOpen(true)} />
            <BillingTotalsBar admission={admission} refreshKey={billingRefresh} />
          </div>
          <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-4 sm:px-6">
            <TabsList className="h-auto bg-transparent p-0 gap-5 rounded-none justify-start">
              <TabsTrigger value="overview" className={TAB_TRIGGER}>Overview</TabsTrigger>
              <TabsTrigger value="vitals" className={TAB_TRIGGER}>Vitals</TabsTrigger>
              <TabsTrigger value="orders" className={TAB_TRIGGER}>Medication orders</TabsTrigger>
              <TabsTrigger value="notes" className={TAB_TRIGGER}>Progress notes</TabsTrigger>
              <TabsTrigger value="attachments" className={TAB_TRIGGER}>Attachments</TabsTrigger>
              <TabsTrigger value="charges" className={TAB_TRIGGER}>Bill</TabsTrigger>
              <TabsTrigger value="discharge" className={TAB_TRIGGER}>Discharge summary</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <div className="lg:flex-1 lg:overflow-auto px-4 sm:px-6 py-4">
          <TabsContent value="overview" className="mt-0">
            <IPDOverviewTab admission={admission} canWrite={canWrite} onChanged={loadAdmission} />
          </TabsContent>

          <TabsContent value="vitals" className="mt-0">
            <IPDVitalsTab admissionId={admissionId} isActive={canWrite && admission.status === 'admitted'} admissionDate={admission?.admission_date} dischargeDate={admission?.discharge_date} />
          </TabsContent>

          <TabsContent value="orders" className="mt-0">
            <IPDMedicationOrdersTab admissionId={admissionId} isActive={canWrite && admission.status === 'admitted'} />
          </TabsContent>

          <TabsContent value="notes" className="mt-0">
            <IPDProgressNotesTab admissionId={admissionId} isActive={canWrite && admission.status === 'admitted'} admissionDate={admission?.admission_date} dischargeDate={admission?.discharge_date} />
          </TabsContent>

          <TabsContent value="attachments" className="mt-0">
            <IPDAttachmentsTab admissionId={admissionId} patientId={admission.patient_id} />
          </TabsContent>

          <TabsContent value="charges" className="mt-0">
            <IPDChargesTab admissionId={admissionId} admission={admission} canWrite={canWrite} refreshKey={billingRefresh} onChargesChanged={() => { setBillingRefresh(v => v + 1); loadAdmission(); }} />
          </TabsContent>

          <TabsContent value="discharge" className="mt-0">
            <IPDDischargeSummaryTab admissionId={admissionId} admissionInfo={admission} onFinalized={loadAdmission} canWrite={canWrite} />
          </TabsContent>
        </div>
      </Tabs>
      )}

      {admission && (
        <TransferModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          admission={admission}
          onTransferred={onStayChanged}
        />
      )}

      {admission && (
        <DischargeModal
          open={dischargeOpen}
          onClose={() => setDischargeOpen(false)}
          admission={admission}
          onUpdated={onStayChanged}
        />
      )}

      {admission && (
        <DatesModal
          open={datesOpen}
          onClose={() => setDatesOpen(false)}
          admission={admission}
          onUpdated={onStayChanged}
        />
      )}
    </div>
  );
}
